// Transport glue for the live-drawing relay, shared by both
// game-agnostic endpoints:
//   - useDrawArtistRelay: streams a DrawingCanvas's actions to the room
//   - useDrawSpectatorRelay: reflects the stream as a DrawingState (DrawingMirror)
// Strokes are presentation-only and never become engine commands/events.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConnectionState,
  type DrawingAction,
  type DrawingState,
  type StrokePoint,
} from '@airhead-games/shared/types';
import { drawingReducer, initialDrawingState } from '@airhead-games/shared';
import { useRoomSocket } from '@/hooks/useRoomSocket';

interface DrawRelayTarget {
  roomCode: string;
  windowId: string;
  // The artist this relay endpoint concerns: the sender when emitting, the
  // artist being mirrored when spectating. Canvases are per-artist — a window
  // can host many simultaneous artists, each with an independent canvas.
  playerId: string;
}

export interface DrawSnapshot {
  strokes: DrawingState['strokes'];
  currentStroke: DrawingState['currentStroke'];
}

/**
 * Returns an `emit` to feed every DrawingCanvas action (via its `onAction` prop).
 * BEGIN/END/CANCEL/UNDO/REDO/CLEAR are relayed as-is; rapid APPEND_POINT actions
 * are coalesced to one APPEND_POINTS batch per animation frame (~30–60fps) to
 * keep the wire light. Local-only tool actions (SET_*) are not relayed.
 */
export function useDrawArtistRelay({
  roomCode,
  windowId,
  playerId,
}: DrawRelayTarget): (action: DrawingAction) => void {
  const { wsClient } = useRoomSocket();
  const pendingPoints = useRef<StrokePoint[]>([]);
  const rafRef = useRef<number | null>(null);

  const send = useCallback(
    (action: Parameters<typeof wsClient.sendDraw>[0]['action']) => {
      wsClient.sendDraw({ roomCode, windowId, playerId, action });
    },
    [wsClient, roomCode, windowId, playerId]
  );

  const flushPoints = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (pendingPoints.current.length === 0) {
      return;
    }
    const points = pendingPoints.current;
    pendingPoints.current = [];
    send({ type: 'APPEND_POINTS', points });
  }, [send]);

  const queuePoint = useCallback(
    (point: StrokePoint) => {
      pendingPoints.current.push(point);
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(flushPoints);
      }
    },
    [flushPoints]
  );

  // Flush any buffered points on unmount so a trailing batch is not lost
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return useCallback(
    (action: DrawingAction) => {
      switch (action.type) {
        case 'BEGIN_STROKE':
          flushPoints();
          send(action);
          break;
        case 'APPEND_POINT':
          queuePoint(action.point);
          break;
        case 'APPEND_POINTS':
          action.points.forEach(queuePoint);
          break;
        case 'END_STROKE':
          flushPoints();
          send({ type: 'END_STROKE' });
          break;
        case 'CANCEL_STROKE':
          // Drop unsent points of the abandoned stroke, then tell spectators
          pendingPoints.current = [];
          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }
          send({ type: 'CANCEL_STROKE' });
          break;
        case 'UNDO':
        case 'REDO':
        case 'CLEAR':
          flushPoints();
          send({ type: action.type });
          break;
        // SET_COLOR / SET_OPACITY / SET_THICKNESS are local tool state only
        default:
          break;
      }
    },
    [send, flushPoints, queuePoint]
  );
}

/**
 * One-shot fetch of the artist's own server-accumulated canvas, for restoring
 * an in-progress drawing after a refresh, phone lockout, or rejoin. Requests a
 * snapshot on mount and resolves with the first matching response; wire the
 * result to DrawingCanvas's `hydrateSnapshot` prop (which only applies it to a
 * still-untouched canvas). Pull-only for v1: strokes drawn while offline are
 * not pushed back to the server.
 */
export function useDrawArtistSnapshot({
  roomCode,
  windowId,
  playerId,
}: DrawRelayTarget): DrawSnapshot | null {
  const { wsClient } = useRoomSocket();
  const [snapshot, setSnapshot] = useState<DrawSnapshot | null>(null);

  useEffect(() => {
    let resolved = false;
    const onSnapshot = (payload: {
      windowId: string;
      playerId: string;
      strokes: DrawingState['strokes'];
      currentStroke: DrawingState['currentStroke'];
    }) => {
      if (
        resolved ||
        payload.windowId !== windowId ||
        payload.playerId !== playerId
      ) {
        return;
      }
      resolved = true;
      setSnapshot({
        strokes: payload.strokes,
        currentStroke: payload.currentStroke,
      });
    };

    // Request now, and again on each connect until answered — after a refresh
    // the canvas usually mounts before the socket is open, and requests sent
    // on a closed socket are silently dropped.
    const request = () => {
      if (!resolved) {
        wsClient.sendDrawSnapshotRequest({ roomCode, windowId, playerId });
      }
    };
    const onConnectionState = (payload: { state: ConnectionState }) => {
      if (payload.state === ConnectionState.CONNECTED) {
        request();
      }
    };

    const offSnapshot = wsClient.on('drawSnapshot', onSnapshot);
    const offConnectionState = wsClient.on(
      'connectionState',
      onConnectionState
    );
    request();
    return () => {
      offSnapshot();
      offConnectionState();
    };
  }, [wsClient, roomCode, windowId, playerId]);

  return snapshot;
}

/**
 * Reflects one artist's live drawing as a DrawingState. Applies that artist's
 * relayed actions through the shared reducer and requests a fresh snapshot on
 * mount and on every (re)connect so a late or reconnecting spectator stays
 * consistent. A window can host many artists at once; mount one spectator
 * relay per canvas being mirrored.
 */
export function useDrawSpectatorRelay({
  roomCode,
  windowId,
  playerId,
}: DrawRelayTarget): DrawingState {
  const { wsClient } = useRoomSocket();
  const [state, setState] = useState<DrawingState>(initialDrawingState);

  useEffect(() => {
    const onDraw = (payload: {
      windowId: string;
      playerId: string;
      action: DrawingAction;
    }) => {
      if (payload.windowId !== windowId || payload.playerId !== playerId) {
        return;
      }
      setState((prev) => drawingReducer(prev, payload.action));
    };
    const onSnapshot = (payload: {
      windowId: string;
      playerId: string;
      strokes: DrawingState['strokes'];
      currentStroke: DrawingState['currentStroke'];
    }) => {
      if (payload.windowId !== windowId || payload.playerId !== playerId) {
        return;
      }
      setState({
        ...initialDrawingState,
        strokes: payload.strokes,
        currentStroke: payload.currentStroke,
      });
    };

    const offDraw = wsClient.on('draw', onDraw);
    const offSnapshot = wsClient.on('drawSnapshot', onSnapshot);
    return () => {
      offDraw();
      offSnapshot();
    };
  }, [wsClient, windowId, playerId]);

  // Request the accumulated canvas now and on each (re)connect
  useEffect(() => {
    const request = () =>
      wsClient.sendDrawSnapshotRequest({ roomCode, windowId, playerId });
    request();
    const onConnectionState = (payload: { state: ConnectionState }) => {
      if (payload.state === ConnectionState.CONNECTED) {
        request();
      }
    };
    const off = wsClient.on('connectionState', onConnectionState);
    return off;
  }, [wsClient, roomCode, windowId, playerId]);

  return state;
}
